import { useEffect } from "react";

/** Анимация «летающих» картинок внутри независимых ромбов. */
export default function useDiamondBounce(
    selector = ".imgForm",
    formSelector = "#form-section form",
    debug = false
) {
    useEffect(() => {
        /* 1 ▸ отключаем на телефонах */
        if (window.matchMedia("(max-width: 767px)").matches) return;

        /* 2 ▸ ждём, пока всё домонтируется */
        const initId = requestAnimationFrame(() => {
            const formEl    = document.querySelector(formSelector);
            const sectionEl = document.getElementById("form-section");
            if (!formEl || !sectionEl) return;

            /* 3 ▸ форма и секция в одних координатах */
            const sRect = sectionEl.getBoundingClientRect();
            const fRect = formEl.getBoundingClientRect();
            const fLeft = fRect.left - sRect.left;
            const fTop  = fRect.top  - sRect.top;   // ← эта строка вернёт fTop
            // вертикальный центр формы относительно секции
            const cYraw = fTop + fRect.height / 2;

            /* сдвиг вниз, чтобы ромбы не залезали на заголовок.
               Подбери опытным путём: 0, 40, 60, 80 …  */
            const SHIFT_Y = -150;

            const cY = cYraw + SHIFT_Y;


            /* 4 ▸ параметры ромбов */
            const DIAG = 560;   // диагональ ромба
            const GAP  = 150;    // зазор до формы
            const d    = DIAG / 2;
            const step = 230;   // вертикальный шаг

            const diamonds = {
                l0: { cx: fLeft - GAP - d,               cy: cY - step,    d },
                l1: { cx: fLeft - GAP - d,               cy: cY,           d },
                l2: { cx: fLeft - GAP - d,               cy: cY + step,    d },
                // l3: { cx: fLeft - GAP - d,               cy: cY + 2*step,  d },

                r0: { cx: fLeft + fRect.width + GAP + d, cy: cY - step,    d },
                r1: { cx: fLeft + fRect.width + GAP + d, cy: cY,           d },
                r2: { cx: fLeft + fRect.width + GAP + d, cy: cY + step,    d },
                // r3: { cx: fLeft + fRect.width + GAP + d, cy: cY + 2*step,  d },
            };


            /* 5 ▸ отладочные рамки */
            if (debug) {
                Object.values(diamonds).forEach(({ cx, cy, d }) => {
                    const box = document.createElement("div");
                    Object.assign(box.style, {
                        position: "absolute",
                        left: `${cx - d}px`,
                        top:  `${cy - d}px`,
                        width: `${2*d}px`,
                        height:`${2*d}px`,
                        border: "1px dashed lime",
                        transform: "rotate(45deg)",
                        pointerEvents: "none",
                        zIndex: 2,
                    });
                    sectionEl.appendChild(box);
                });
            }

            /* 6 ▸ точка внутри ромба (учёт радиуса) */
            const MARGIN = 4;         // px
            const inside = ({ x, y, r }, { cx, cy, d }) =>
                Math.abs(x - cx) + Math.abs(y - cy) < d - r - MARGIN;

            /* 7 ▸ собираем картинки */
            const els = [...sectionEl.querySelectorAll(selector)];
            if (!els.length) {
                console.warn("DiamondBounce: элементов по селектору нет:", selector);
                return;
            }

            const items = els.map((el, i) => {
                const fall = i < 4 ? `l${i}` : `r${i - 4}`;
                const key  = el.dataset.diamond ?? fall;
                let arena = diamonds[key];

                const r  = Math.hypot(el.offsetWidth, el.offsetHeight)/2;
                const speed = +(el.dataset.speed ?? (0.25 + Math.random()*0.35)); // 15-35 px/с
                const angle = +(el.dataset.angle ?? Math.random()*Math.PI*2);
                const vx = Math.cos(angle)*speed;
                const vy = Math.sin(angle)*speed;

                if (!arena) {
                    const cx = +el.dataset.cx;
                    const cy = +el.dataset.cy;
                    const d  = +el.dataset.d  || DIAG/2;
                    if (Number.isFinite(cx) && Number.isFinite(cy)) {
                        arena = { cx, cy, d };
                        diamonds[key] = arena;
                        return { el, x: cx, y: cy, vx, vy, r, arena };
                    }
                    console.warn(`⚠️ Ромб "${key}" не найден и координаты не заданы`, el);
                    return null;
                }

                const { cx, cy } = arena;
                el.style.left = "0";
                el.style.top = "0";
                el.style.transform = `translate(${cx - r}px, ${cy - r}px)`;

                return { el, x: cx, y: cy, vx, vy, r, arena };

            }).filter(Boolean);

            /* 8 ▸ отражение от грани ромба */
            const reflect = (o) => {
                const nx = Math.sign(o.x - o.arena.cx);
                const ny = Math.sign(o.y - o.arena.cy);
                const nX = nx / Math.SQRT2;
                const nY = ny / Math.SQRT2;
                const dot = o.vx*nX + o.vy*nY;
                o.vx -= 2*dot*nX;
                o.vy -= 2*dot*nY;
            };

            /* 9 ▸ rAF-цикл */
            const MAX_DT = 0.05;          // ≤50 мс
            let raf;
            const loop = (prev) => (now) => {
                let dt = (now - prev)/1000;
                if (dt > MAX_DT) dt = MAX_DT;

                items.forEach((o) => {
                    o.x += o.vx*dt*60;
                    o.y += o.vy*dt*60;

                    if (!inside(o, o.arena)) {
                        reflect(o);
                        /* мягко вернём внутрь */
                        const excess = Math.abs(o.x-o.arena.cx) + Math.abs(o.y-o.arena.cy) - (o.arena.d - o.r);
                        if (excess > 0) {
                            const nx = Math.sign(o.x - o.arena.cx);
                            const ny = Math.sign(o.y - o.arena.cy);
                            o.x -= nx*excess*0.6;
                            o.y -= ny*excess*0.6;
                        }
                    }

                    o.el.style.transform = `translate(${o.x - o.r}px, ${o.y - o.r}px)`;
                });

                raf = requestAnimationFrame(loop(now));
            };
            raf = requestAnimationFrame(loop(performance.now()));

            /* 10 ▸ очистка */
            return () => cancelAnimationFrame(raf);
        });

        return () => cancelAnimationFrame(initId);
    }, [selector, formSelector, debug]);
}
